import { Pane } from 'tweakpane'
import * as EssentialsPlugin from '@tweakpane/plugin-essentials'
import { hexToRgb, rgbToHex, toSerializable } from './params'

const PRESET_STORAGE_KEY = 'encore-background-presets'
const SEED_STORAGE_KEY = 'encore-background-seeds'

function paramsToProxy(params, playing, presetName, seedName) {
  return {
    scale: params.scale,
    blur: params.blur,
    warp: params.warp,
    spread: params.spread,
    soft: params.soft,
    lateral: params.lateral,
    seed: params.seed,
    seedName,
    haze: rgbToHex(params.haze),
    wash: rgbToHex(params.wash),
    mist: rgbToHex(params.mist),
    ink: rgbToHex(params.ink),
    olive: rgbToHex(params.olive),
    acid: rgbToHex(params.acid),
    band1: params.band1,
    band2: params.band2,
    band3: params.band3,
    band4: params.band4,
    band5: params.band5,
    grain: params.grain,
    grainSize: params.grainSize,
    drift: params.drift,
    loopDuration: params.loopDuration,
    playing,
    presetName,
  }
}

function proxyToParams(proxy) {
  return {
    scale: proxy.scale,
    blur: proxy.blur,
    warp: proxy.warp,
    spread: proxy.spread,
    soft: proxy.soft,
    lateral: proxy.lateral,
    seed: proxy.seed,
    haze: hexToRgb(proxy.haze),
    wash: hexToRgb(proxy.wash),
    mist: hexToRgb(proxy.mist),
    ink: hexToRgb(proxy.ink),
    olive: hexToRgb(proxy.olive),
    acid: hexToRgb(proxy.acid),
    band1: proxy.band1,
    band2: proxy.band2,
    band3: proxy.band3,
    band4: proxy.band4,
    band5: proxy.band5,
    grain: proxy.grain,
    grainSize: proxy.grainSize,
    drift: proxy.drift,
    loopDuration: proxy.loopDuration,
  }
}

function readStore(key) {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return {}
    const data = JSON.parse(raw)
    return data && typeof data === 'object' ? data : {}
  } catch {
    return {}
  }
}

function writeStore(key, data) {
  window.localStorage.setItem(key, JSON.stringify(data))
}

/**
 * Imperative Tweakpane UI for the background module.
 * Returns { dispose, fpsBegin, fpsEnd, refresh }.
 */
export function createPanel({
  params,
  playing,
  onParamsChange,
  onPlayingChange,
  onExportStill,
  builtinPresets = {},
}) {
  const pane = new Pane({ title: 'Grain Gradient Studio', expanded: true })
  pane.registerPlugin(EssentialsPlugin)

  const proxy = paramsToProxy(params, playing, 'blush', 'blush')

  const fps = pane.addBlade({
    view: 'fpsgraph',
    label: 'FPS',
    rows: 2,
  })

  const field = pane.addFolder({ title: 'Field' })
  field.addBinding(proxy, 'scale', { min: 0.3, max: 3, step: 0.01 })
  field.addBinding(proxy, 'blur', { min: 0, max: 4, step: 0.01 })
  field.addBinding(proxy, 'warp', { min: 0, max: 6, step: 0.01 })
  field.addBinding(proxy, 'spread', { min: 0, max: 1.5, step: 0.01 })
  field.addBinding(proxy, 'soft', { min: 0, max: 1, step: 0.01, label: 'softness' })
  field.addBinding(proxy, 'lateral', {
    min: 0,
    max: 1,
    step: 0.01,
    label: 'lateral wind',
  })
  field.addBinding(proxy, 'seed', { min: 0, max: 20, step: 0.01 })
  field.addBinding(proxy, 'seedName', { label: 'seed name' })
  field.addButton({ title: 'Randomise seed' }).on('click', () => {
    proxy.seed = Math.round(Math.random() * 2000) / 100
    pane.refresh()
    onParamsChange(proxyToParams(proxy))
  })
  field.addButton({ title: 'Copy seed' }).on('click', async () => {
    const text = String(proxy.seed)
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      window.prompt('Copy seed:', text)
    }
  })
  field.addButton({ title: 'Save seed' }).on('click', () => {
    const name = proxy.seedName || 'seed'
    const seeds = readStore(SEED_STORAGE_KEY)
    seeds[name] = proxy.seed
    writeStore(SEED_STORAGE_KEY, seeds)
  })
  field.addButton({ title: 'Load seed' }).on('click', () => {
    const seeds = readStore(SEED_STORAGE_KEY)
    const names = Object.keys(seeds)
    const name = proxy.seedName
    let pick = name
    if (!(name in seeds)) {
      if (!names.length) {
        window.alert('No saved seeds in localStorage')
        return
      }
      pick = window.prompt(`Seeds: ${names.join(', ')}\nName to load:`, names[0]) || ''
    }
    if (!pick || !(pick in seeds)) return
    proxy.seedName = pick
    proxy.seed = seeds[pick]
    pane.refresh()
    onParamsChange(proxyToParams(proxy))
  })

  const colour = pane.addFolder({ title: 'Colour' })
  colour.addBinding(proxy, 'haze', { view: 'color', label: '1 haze · top' })
  colour.addBinding(proxy, 'wash', { view: 'color', label: '2 wash' })
  colour.addBinding(proxy, 'mist', { view: 'color', label: '3 mist' })
  colour.addBinding(proxy, 'ink', { view: 'color', label: '4 ink' })
  colour.addBinding(proxy, 'olive', { view: 'color', label: '5 olive' })
  colour.addBinding(proxy, 'acid', { view: 'color', label: '6 acid · bottom' })
  colour.addBinding(proxy, 'band1', { min: 0, max: 1, step: 0.01, label: 'band1 (↓1→2)' })
  colour.addBinding(proxy, 'band2', { min: 0, max: 1, step: 0.01, label: 'band2 (↓2→3)' })
  colour.addBinding(proxy, 'band3', { min: 0, max: 1, step: 0.01, label: 'band3 (↓3→4)' })
  colour.addBinding(proxy, 'band4', { min: 0, max: 1, step: 0.01, label: 'band4 (↓4→5)' })
  colour.addBinding(proxy, 'band5', { min: 0, max: 1, step: 0.01, label: 'band5 (↓5→6)' })

  const grain = pane.addFolder({ title: 'Grain' })
  grain.addBinding(proxy, 'grain', { min: 0, max: 0.45, step: 0.005, label: 'amount' })
  grain.addBinding(proxy, 'grainSize', {
    min: 0.5,
    max: 4,
    step: 0.1,
    label: 'size',
  })

  const motion = pane.addFolder({ title: 'Motion' })
  motion.addBinding(proxy, 'drift', { min: 0, max: 1, step: 0.01, label: 'drift radius' })
  motion.addBinding(proxy, 'loopDuration', {
    min: 1,
    max: 30,
    step: 0.1,
    label: 'loop duration',
  })
  motion.addBinding(proxy, 'playing', { label: 'play' })

  const exp = pane.addFolder({ title: 'Export' })
  exp.addBinding(proxy, 'presetName', { label: 'preset' })
  exp.addButton({ title: 'Save preset' }).on('click', () => {
    const name = proxy.presetName || 'preset'
    const presets = readStore(PRESET_STORAGE_KEY)
    presets[name] = toSerializable({ ...params, ...proxyToParams(proxy) })
    writeStore(PRESET_STORAGE_KEY, presets)
  })
  exp.addButton({ title: 'Load preset' }).on('click', () => {
    const stored = readStore(PRESET_STORAGE_KEY)
    const names = [...Object.keys(builtinPresets), ...Object.keys(stored)]
    const unique = [...new Set(names)]
    const name = proxy.presetName
    let pick = name
    if (!unique.includes(name)) {
      if (!unique.length) {
        window.alert('No presets found')
        return
      }
      pick = window.prompt(`Presets: ${unique.join(', ')}\nName to load:`, unique[0]) || ''
    }
    if (!pick) return
    const data = builtinPresets[pick] || stored[pick]
    if (!data) {
      window.alert(`Preset "${pick}" not found`)
      return
    }
    proxy.presetName = pick
    Object.assign(proxy, paramsToProxy({ ...params, ...data }, proxy.playing, pick, proxy.seedName))
    pane.refresh()
    onParamsChange(proxyToParams(proxy))
  })
  exp.addButton({ title: 'Export still' }).on('click', () => onExportStill())

  pane.on('change', () => {
    onParamsChange(proxyToParams(proxy))
    onPlayingChange(proxy.playing)
  })

  return {
    dispose: () => pane.dispose(),
    fpsBegin: () => fps.begin(),
    fpsEnd: () => fps.end(),
    refresh: (nextParams, nextPlaying) => {
      Object.assign(
        proxy,
        paramsToProxy(nextParams, nextPlaying, proxy.presetName, proxy.seedName),
      )
      pane.refresh()
    },
  }
}
